import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { getProgrammingLesson, submitProgrammingCode, requestHint, getProgrammingSubmissions, generateSimilarTasks, requestAdditionalResources } from '../../services/api';
import { formatInstructions } from '../../utils/contentFormatter';
import api from '../../services/api';
import CodeEditor from '../../components/CodeEditor/CodeEditor';
import MultiCodeEditor from '../../components/MultiCodeEditor/MultiCodeEditor';
import HintModal from '../../components/HintModal/HintModal';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import LessonSummary from '../../components/LessonSummary/LessonSummary';
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle';
import './ProgrammingLesson.css';

const ProgrammingLesson = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { handleRequest } = useApi();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(true);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [code, setCode] = useState({});
  const [htmlCode, setHtmlCode] = useState({});
  const [cssCode, setCssCode] = useState({});
  const [jsCode, setJsCode] = useState({});
  const [submissions, setSubmissions] = useState({});
  const [validationResults, setValidationResults] = useState({});
  const [hintModalOpen, setHintModalOpen] = useState(false);
  const [hints, setHints] = useState({});
  const [hintLoading, setHintLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [showResources, setShowResources] = useState({});
  const [additionalResources, setAdditionalResources] = useState({});
  const [showResourceRequest, setShowResourceRequest] = useState({});
  const [resourceQuestion, setResourceQuestion] = useState({});
  const [loadingAdditionalResources, setLoadingAdditionalResources] = useState({});
  const [consoleOutput, setConsoleOutput] = useState({}); // { taskNumber: [output lines] }
  const [isRunning, setIsRunning] = useState({}); // { taskNumber: boolean }
  const messageHandlersRef = useRef({}); // { taskNumber: handler function }
  const continuancePrefilledRef = useRef(new Set());

  useEffect(() => {
    // Reset all state when lesson ID changes
    setCurrentTaskIndex(0);
    setCode({});
    setHtmlCode({});
    setCssCode({});
    setJsCode({});
    setSubmissions({});
    setValidationResults({});
    setHints({});
    setShowSummary(false);
    setErrorMessage('');
    setValidating(false);
    setHintModalOpen(false);
    setShowResources({});
    setAdditionalResources({});
    setShowResourceRequest({});
    setResourceQuestion({});
    setLoadingAdditionalResources({});
    continuancePrefilledRef.current = new Set();

    const fetchLesson = async () => {
      try {
        setLoadingLesson(true);
        const response = await handleRequest(getProgrammingLesson, id);
        setLesson(response.data);
        
        // Initialize code with starter code or empty
        const initialCode = {};
        const initialHtml = {};
        const initialCss = {};
        const initialJs = {};
        response.data.tasks.forEach(task => {
          if (response.data.language === 'mixed') {
            initialHtml[task.taskNumber] = task.starterCodeHTML || task.starterCode || '';
            initialCss[task.taskNumber] = task.starterCodeCSS || '';
            initialJs[task.taskNumber] = task.starterCodeJS || '';
          } else {
            initialCode[task.taskNumber] = task.starterCode || '';
          }
        });
        setCode(initialCode);
        setHtmlCode(initialHtml);
        setCssCode(initialCss);
        setJsCode(initialJs);

        // Load existing submissions
        try {
          const submissionsResponse = await handleRequest(
            getProgrammingSubmissions,
            id
          );
          const subs = submissionsResponse.data || [];
          const subsMap = {};
          const hintsMap = {};
          subs.forEach(sub => {
            subsMap[sub.taskNumber] = sub;
            if (sub.studentCode) {
              // For mixed language, we'd need to parse the combined code
              // For now, just set it to the combined code
              if (response.data.language === 'mixed') {
                // Try to extract HTML, CSS, JS from combined code
                const combined = sub.studentCode;
                const htmlMatch = combined.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                const cssMatch = combined.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
                const jsMatch = combined.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
                
                if (htmlMatch) {
                  setHtmlCode(prev => ({
                    ...prev,
                    [sub.taskNumber]: htmlMatch[1].trim()
                  }));
                }
                if (cssMatch) {
                  setCssCode(prev => ({
                    ...prev,
                    [sub.taskNumber]: cssMatch[1].trim()
                  }));
                }
                if (jsMatch) {
                  setJsCode(prev => ({
                    ...prev,
                    [sub.taskNumber]: jsMatch[1].trim()
                  }));
                }
              } else {
                setCode(prev => ({
                  ...prev,
                  [sub.taskNumber]: sub.studentCode
                }));
              }
            }
            if (sub.validationResult) {
              setValidationResults(prev => ({
                ...prev,
                [sub.taskNumber]: sub.validationResult
              }));
            }
            if (sub.hints && sub.hints.length > 0) {
              hintsMap[sub.taskNumber] = sub.hints;
            }
          });
          setSubmissions(subsMap);
          setHints(hintsMap);
        } catch (err) {
          // No submissions yet, that's okay
        }
      } catch (error) {
        setErrorMessage('Kunne ikke laste inn programmeringsleksjonen');
        console.error('Failed to fetch lesson:', error);
      } finally {
        setLoadingLesson(false);
      }
    };

    fetchLesson();
  }, [id, handleRequest]);

  const currentTask = lesson?.tasks[currentTaskIndex];

  const isUnmodifiedStarterForTask = (task) => {
    if (!task || !lesson) return true;
    const taskNum = task.taskNumber;
    if (lesson.language === 'mixed') {
      const currentHtml = (htmlCode[taskNum] ?? '').trim();
      const currentCss = (cssCode[taskNum] ?? '').trim();
      const currentJs = (jsCode[taskNum] ?? '').trim();
      const starterHtml = (task.starterCodeHTML || task.starterCode || '').trim();
      const starterCss = (task.starterCodeCSS || '').trim();
      const starterJs = (task.starterCodeJS || '').trim();
      return currentHtml === starterHtml && currentCss === starterCss && currentJs === starterJs;
    }
    const current = (code[taskNum] ?? '').trim();
    const starter = (task.starterCode || '').trim();
    return current === starter;
  };

  useEffect(() => {
    if (!lesson || !currentTask) return;
    if (!currentTask.continuance) return;
    const taskNum = currentTask.taskNumber;
    if (!Number.isFinite(taskNum) || taskNum <= 1) return;
    if (continuancePrefilledRef.current.has(taskNum)) return;

    // Only prefill if the student hasn't already submitted this task and hasn't modified its starter code
    if (submissions?.[taskNum]) {
      continuancePrefilledRef.current.add(taskNum);
      return;
    }
    if (!isUnmodifiedStarterForTask(currentTask)) {
      continuancePrefilledRef.current.add(taskNum);
      return;
    }

    const prevNum = taskNum - 1;
    if (lesson.language === 'mixed') {
      const prevHtml = htmlCode[prevNum] ?? '';
      const prevCss = cssCode[prevNum] ?? '';
      const prevJs = jsCode[prevNum] ?? '';
      if ((prevHtml || prevCss || prevJs) && (prevHtml.trim() || prevCss.trim() || prevJs.trim())) {
        setHtmlCode(prev => ({ ...prev, [taskNum]: prevHtml }));
        setCssCode(prev => ({ ...prev, [taskNum]: prevCss }));
        setJsCode(prev => ({ ...prev, [taskNum]: prevJs }));
      }
    } else {
      const prevCode = code[prevNum] || submissions?.[prevNum]?.studentCode || '';
      if (prevCode && prevCode.trim()) {
        setCode(prev => ({ ...prev, [taskNum]: prevCode }));
      }
    }

    continuancePrefilledRef.current.add(taskNum);
  }, [currentTaskIndex, lesson, submissions]); // intentionally NOT depending on code/html/css/js to avoid overwrites while typing

  const handleCodeChange = (value) => {
    if (currentTask) {
      setCode(prev => ({
        ...prev,
        [currentTask.taskNumber]: value
      }));
    }
  };

  const handleHtmlChange = (value) => {
    if (currentTask) {
      setHtmlCode(prev => ({
        ...prev,
        [currentTask.taskNumber]: value
      }));
    }
  };

  const handleCssChange = (value) => {
    if (currentTask) {
      setCssCode(prev => ({
        ...prev,
        [currentTask.taskNumber]: value
      }));
    }
  };

  const handleJsChange = (value) => {
    if (currentTask) {
      setJsCode(prev => ({
        ...prev,
        [currentTask.taskNumber]: value
      }));
    }
  };

  const handleReset = () => {
    if (!currentTask) return;
    
    if (window.confirm('Er du sikker på at du vil tilbakestille koden til startkoden? Alle endringer vil gå tapt.')) {
      if (lesson.language === 'mixed') {
        setHtmlCode(prev => ({
          ...prev,
          [currentTask.taskNumber]: currentTask.starterCodeHTML || currentTask.starterCode || ''
        }));
        setCssCode(prev => ({
          ...prev,
          [currentTask.taskNumber]: currentTask.starterCodeCSS || ''
        }));
        setJsCode(prev => ({
          ...prev,
          [currentTask.taskNumber]: currentTask.starterCodeJS || ''
        }));
      } else {
        setCode(prev => ({
          ...prev,
          [currentTask.taskNumber]: currentTask.starterCode || ''
        }));
      }
      // Clear validation result for this task when resetting
      setValidationResults(prev => {
        const newResults = { ...prev };
        delete newResults[currentTask.taskNumber];
        return newResults;
      });
    }
  };

  const handleSubmit = async () => {
    if (!currentTask) return;

    let currentCode = '';
    if (lesson.language === 'mixed') {
      const html = htmlCode[currentTask.taskNumber] || '';
      const css = cssCode[currentTask.taskNumber] || '';
      const js = jsCode[currentTask.taskNumber] || '';
      
      // Combine HTML, CSS, and JS into a single HTML document
      currentCode = `<!DOCTYPE html>
<html>
<head>
  <style>
${css}
  </style>
</head>
<body>
${html}
  <script>
${js}
  </script>
</body>
</html>`;
      
      if (!html.trim() && !css.trim() && !js.trim()) {
        setErrorMessage('Skriv litt kode før du leverer');
        return;
      }
    } else {
      currentCode = code[currentTask.taskNumber] || '';
      if (!currentCode.trim()) {
        setErrorMessage('Skriv litt kode før du leverer');
        return;
      }
    }

    try {
      setErrorMessage('');
      setValidating(true);
      
      // Call API directly to avoid global loading state from useApi
      // The endpoint requires auth, so student ID will be saved automatically via req.userId
      const response = await api.post('/programming-submissions', {
        programmingLessonId: id,
        taskNumber: currentTask.taskNumber,
        studentCode: currentCode
      });
      
      const validationResult = response.data.data.validationResult;
      setValidationResults(prev => ({
        ...prev,
        [currentTask.taskNumber]: validationResult
      }));
      setSubmissions(prev => ({
        ...prev,
        [currentTask.taskNumber]: response.data.data
      }));
    } catch (error) {
      // Handle authentication errors specifically
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorMessage('Du må være innlogget for å levere kode. Logg inn og prøv igjen.');
      } else {
        setErrorMessage(error.response?.data?.message || error.message || 'Kunne ikke validere koden');
      }
    } finally {
      setValidating(false);
    }
  };

  const handleRequestHint = async () => {
    if (!currentTask) return;

    const currentCode = code[currentTask.taskNumber] || '';
    const submission = submissions[currentTask.taskNumber];
    
    if (!submission || !submission.id) {
      setErrorMessage('Lever koden minst én gang før du ber om et hint');
      return;
    }

    try {
      setHintLoading(true);
      setErrorMessage('');
      const response = await handleRequest(
        requestHint,
        submission.id
      );
      
      // handleRequest returns response.data, so the hint is at response.data.hint
      // Extract hint text - handle different response structures
      let newHint = null;
      if (typeof response === 'string') {
        newHint = response;
      } else if (response?.data?.hint) {
        newHint = response.data.hint;
      } else if (response?.hint) {
        newHint = response.hint;
      } else if (response?.data) {
        newHint = typeof response.data === 'string' ? response.data : String(response.data);
      } else {
        newHint = String(response);
      }
      
      if (!newHint || typeof newHint !== 'string') {
        throw new Error('Kunne ikke hente hint fra serveren');
      }
      setHints(prev => ({
        ...prev,
        [currentTask.taskNumber]: [
          ...(prev[currentTask.taskNumber] || []),
          { hint: newHint, requestedAt: new Date() }
        ]
      }));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere hint');
    } finally {
      setHintLoading(false);
    }
  };

  const handleNext = () => {
    if (currentTaskIndex < lesson.tasks.length - 1) {
      setCurrentTaskIndex(prev => prev + 1);
      setErrorMessage('');
    } else {
      // Show summary when on last task
      setShowSummary(true);
    }
  };

  const handlePrevious = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(prev => prev - 1);
      setErrorMessage('');
      setShowSummary(false);
    }
  };

  const handleRequestAdditionalResources = async () => {
    if (!currentTask || !lesson) return;

    const question = resourceQuestion[currentTask.taskNumber] || '';
    if (!question.trim()) {
      setErrorMessage('Skriv inn et spørsmål om hvilke ekstra ressurser du trenger');
      return;
    }

    try {
      setErrorMessage('');
      setLoadingAdditionalResources(prev => ({
        ...prev,
        [currentTask.taskNumber]: true
      }));

      const response = await handleRequest(
        requestAdditionalResources,
        id,
        currentTask.taskNumber,
        question
      );

      setAdditionalResources(prev => ({
        ...prev,
        [currentTask.taskNumber]: [
          ...(prev[currentTask.taskNumber] || []),
          {
            question,
            answer: response.data.additionalResources,
            requestedAt: new Date()
          }
        ]
      }));

      setResourceQuestion(prev => ({
        ...prev,
        [currentTask.taskNumber]: ''
      }));
      setShowResourceRequest(prev => ({
        ...prev,
        [currentTask.taskNumber]: false
      }));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere ekstra ressurser');
    } finally {
      setLoadingAdditionalResources(prev => ({
        ...prev,
        [currentTask.taskNumber]: false
      }));
    }
  };

  const handleRedo = async (difficulties) => {
    try {
      setErrorMessage('');
      // Generate new similar tasks and create a new lesson
      const response = await handleRequest(
        generateSimilarTasks,
        id,
        difficulties,
        lesson.language,
        lesson.level || 'basic',
        3
      );
      
      // Navigate to the new lesson
      const newLessonId = response.data.id || response.data._id;
      navigate(`/programming-lessons/${newLessonId}`, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere lignende oppgaver');
    }
  };

  const handleCloseSummary = () => {
    setShowSummary(false);
  };

  const handleRunCode = () => {
    if (!currentTask) return;
    const taskNum = currentTask.taskNumber;
    
    setIsRunning(prev => ({ ...prev, [taskNum]: true }));
    setConsoleOutput(prev => ({ ...prev, [taskNum]: [] }));
    
    // Use setTimeout to ensure state is updated before execution
    setTimeout(() => {
      try {
        let codeToRun = '';
        let isHTML = false;
        
        if (lesson.language === 'mixed') {
          const html = htmlCode[taskNum] || '';
          const css = cssCode[taskNum] || '';
          const js = jsCode[taskNum] || '';
          
          // Create a complete HTML document with console capture
          codeToRun = `<!DOCTYPE html>
<html>
<head>
  <style>
${css}
  </style>
</head>
<body>
${html}
  <script>
    (function() {
      const outputs = [];
      const addOutput = (type, ...args) => {
        const message = args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg, null, 2);
            } catch (e) {
              return String(arg);
            }
          }
          return String(arg);
        }).join(' ');
        outputs.push({ type, message, timestamp: new Date().toISOString() });
      };
      
      // Override console methods BEFORE any code runs
      const originalLog = console.log.bind(console);
      const originalError = console.error.bind(console);
      const originalWarn = console.warn.bind(console);
      const originalInfo = console.info.bind(console);
      
      console.log = function(...args) {
        originalLog(...args);
        addOutput('log', ...args);
        sendOutputs();
      };
      
      console.error = function(...args) {
        originalError(...args);
        addOutput('error', ...args);
        sendOutputs();
      };
      
      console.warn = function(...args) {
        originalWarn(...args);
        addOutput('warn', ...args);
        sendOutputs();
      };
      
      console.info = function(...args) {
        originalInfo(...args);
        addOutput('info', ...args);
        sendOutputs();
      };
      
      const sendOutputs = () => {
        if (window.parent && window.parent.postMessage) {
          window.parent.postMessage({ 
            type: 'console-output', 
            taskNum: ${taskNum},
            outputs: [...outputs] 
          }, '*');
        }
      };
      
      // Execute user code
      try {
${js}
      } catch (error) {
        addOutput('error', error.toString() + '\\n' + error.stack);
        sendOutputs();
      }
      
      // Send final outputs after a short delay to catch any async console calls
      setTimeout(() => {
        sendOutputs();
      }, 100);
    })();
  </script>
</body>
</html>`;
          isHTML = true;
        } else if (lesson.language === 'javascript') {
          codeToRun = code[taskNum] || '';
      } else if (lesson.language === 'html') {
        let html = code[taskNum] || '';
        
        // Pre-process HTML: wrap all inline script tags BEFORE injecting console capture
        // This ensures our console override is in place before user scripts run
        html = html.replace(/<script([^>]*)>([\s\S]*?)<\/script>/gi, (match, attributes, scriptContent) => {
          // Skip external scripts (with src attribute)
          if (attributes && attributes.includes('src')) {
            return match;
          }
          // Skip if already wrapped
          if (attributes && attributes.includes('data-console-capture')) {
            return match;
          }
          // Wrap inline script content
          return `<script${attributes} data-console-capture="true">(function() {
            try {
              ${scriptContent}
            } catch (error) {
              console.error(error.toString() + '\\n' + error.stack);
            }
          })();</script>`;
        });
        
        // Inject console capture script at the START of head or body
        const consoleCaptureScript = `<script>
          (function() {
            const outputs = [];
            const addOutput = (type, ...args) => {
              const message = args.map(arg => {
                if (typeof arg === 'object') {
                  try {
                    return JSON.stringify(arg, null, 2);
                  } catch (e) {
                    return String(arg);
                  }
                }
                return String(arg);
              }).join(' ');
              outputs.push({ type, message, timestamp: new Date().toISOString() });
            };
            
            // Override console methods IMMEDIATELY - this must happen before any other scripts
            const originalLog = console.log.bind(console);
            const originalError = console.error.bind(console);
            const originalWarn = console.warn.bind(console);
            const originalInfo = console.info.bind(console);
            
            console.log = function(...args) {
              originalLog(...args);
              addOutput('log', ...args);
              sendOutputs();
            };
            
            console.error = function(...args) {
              originalError(...args);
              addOutput('error', ...args);
              sendOutputs();
            };
            
            console.warn = function(...args) {
              originalWarn(...args);
              addOutput('warn', ...args);
              sendOutputs();
            };
            
            console.info = function(...args) {
              originalInfo(...args);
              addOutput('info', ...args);
              sendOutputs();
            };
            
            const sendOutputs = () => {
              if (window.parent && window.parent.postMessage) {
                window.parent.postMessage({ 
                  type: 'console-output', 
                  taskNum: ${taskNum},
                  outputs: [...outputs] 
                }, '*');
              }
            };
            
            // Send outputs after DOM is ready to catch any console calls
            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', () => {
                setTimeout(() => sendOutputs(), 300);
              });
            } else {
              setTimeout(() => sendOutputs(), 300);
            }
            
            window.addEventListener('load', () => {
              setTimeout(() => sendOutputs(), 300);
            });
            
            // Also send periodically to catch delayed console calls
            setInterval(() => {
              if (outputs.length > 0) {
                sendOutputs();
              }
            }, 500);
          })();
        </script>`;
        
        // Insert console capture at the VERY BEGINNING of HTML, before any user scripts
        // This ensures our console override runs first
        let htmlWithConsoleCapture;
        if (html.includes('<!DOCTYPE') || html.includes('<html')) {
          // If HTML has DOCTYPE or html tag, inject right after <html> or in <head>
          if (html.includes('<head')) {
            htmlWithConsoleCapture = html.replace(/<head[^>]*>/i, (match) => match + consoleCaptureScript);
          } else if (html.includes('<html')) {
            // Inject right after <html> tag
            htmlWithConsoleCapture = html.replace(/<html[^>]*>/i, (match) => match + '<head>' + consoleCaptureScript + '</head>');
          } else {
            // Prepend to the beginning
            htmlWithConsoleCapture = consoleCaptureScript + html;
          }
        } else {
          // No HTML structure, wrap it properly with console capture in head
          htmlWithConsoleCapture = `<!DOCTYPE html>
<html>
<head>
${consoleCaptureScript}
</head>
<body>
${html}
</body>
</html>`;
        }
        
        codeToRun = htmlWithConsoleCapture;
        isHTML = true;
      }
        
        if (isHTML) {
          // Remove any existing handler for this task
          if (messageHandlersRef.current[taskNum]) {
            window.removeEventListener('message', messageHandlersRef.current[taskNum]);
          }
          
          // For HTML, create an iframe to execute it
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.id = `console-iframe-${taskNum}`;
          iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin'); // Allow scripts and same-origin access
          
          const messageHandler = (event) => {
            // Accept messages from any origin (since iframe srcdoc creates a unique origin)
            // Verify it matches the task number
            if (event.data && event.data.type === 'console-output' && event.data.taskNum === taskNum) {
              setConsoleOutput(prev => {
                const existingOutputs = prev[taskNum] || [];
                const newOutputs = event.data.outputs || [];
                // Merge outputs, avoiding duplicates based on message and timestamp
                const mergedOutputs = [...existingOutputs];
                newOutputs.forEach(newOutput => {
                  const isDuplicate = mergedOutputs.some(
                    existing => existing.message === newOutput.message && 
                               existing.timestamp === newOutput.timestamp
                  );
                  if (!isDuplicate) {
                    mergedOutputs.push(newOutput);
                  }
                });
                return {
                  ...prev,
                  [taskNum]: mergedOutputs
                };
              });
            }
          };
          
          // Store handler reference
          messageHandlersRef.current[taskNum] = messageHandler;
          window.addEventListener('message', messageHandler);
          
          // Set srcdoc after listener is set up
          iframe.onload = () => {
            // Give it time to execute and send messages
            setTimeout(() => {
              setIsRunning(prev => ({ ...prev, [taskNum]: false }));
            }, 1000);
          };
          
          iframe.srcdoc = codeToRun;
          document.body.appendChild(iframe);
          
          // Cleanup after 10 seconds
          setTimeout(() => {
            if (messageHandlersRef.current[taskNum]) {
              window.removeEventListener('message', messageHandlersRef.current[taskNum]);
              delete messageHandlersRef.current[taskNum];
            }
            const iframeEl = document.getElementById(`console-iframe-${taskNum}`);
            if (iframeEl && document.body.contains(iframeEl)) {
              document.body.removeChild(iframeEl);
            }
            setIsRunning(prev => ({ ...prev, [taskNum]: false }));
          }, 10000);
        } else {
          // For pure JavaScript, execute directly with console capture
          const outputs = [];
          const originalLog = console.log.bind(console);
          const originalError = console.error.bind(console);
          const originalWarn = console.warn.bind(console);
          const originalInfo = console.info.bind(console);
          
          const addOutput = (type, ...args) => {
            const message = args.map(arg => {
              if (typeof arg === 'object') {
                try {
                  return JSON.stringify(arg, null, 2);
                } catch (e) {
                  return String(arg);
                }
              }
              return String(arg);
            }).join(' ');
            outputs.push({
              type,
              message,
              timestamp: new Date().toISOString()
            });
            // Update state immediately for each output
            setConsoleOutput(prev => ({
              ...prev,
              [taskNum]: [...outputs]
            }));
          };
          
          console.log = function(...args) {
            originalLog(...args);
            addOutput('log', ...args);
          };
          
          console.error = function(...args) {
            originalError(...args);
            addOutput('error', ...args);
          };
          
          console.warn = function(...args) {
            originalWarn(...args);
            addOutput('warn', ...args);
          };
          
          console.info = function(...args) {
            originalInfo(...args);
            addOutput('info', ...args);
          };
          
          try {
            // Wrap in a function to catch errors
            const result = eval(codeToRun);
            if (result !== undefined && result !== null) {
              addOutput('log', result);
            }
          } catch (error) {
            addOutput('error', error.toString() + '\n' + (error.stack || ''));
          } finally {
            // Restore original console methods
            console.log = originalLog;
            console.error = originalError;
            console.warn = originalWarn;
            console.info = originalInfo;
            
            setIsRunning(prev => ({ ...prev, [taskNum]: false }));
          }
        }
      } catch (error) {
        setConsoleOutput(prev => ({
          ...prev,
          [taskNum]: [{ type: 'error', message: error.toString(), timestamp: new Date().toISOString() }]
        }));
        setIsRunning(prev => ({ ...prev, [taskNum]: false }));
      }
    }, 0);
  };

  if (loadingLesson || !lesson) {
    return <div className="programming-lesson-container">Laster...</div>;
  }

  // Show summary if on summary page
  if (showSummary) {
    return (
      <LessonSummary
        lesson={lesson}
        validationResults={validationResults}
        submissions={submissions}
        onRedo={handleRedo}
        onClose={handleCloseSummary}
        errorMessage={errorMessage}
      />
    );
  }

  const currentValidation = validationResults[currentTask?.taskNumber];
  const currentHints = hints[currentTask?.taskNumber] || [];

  return (
    <div className="programming-lesson-container">
      <header className="lesson-header">
        <div className="lesson-header-top">
          <button 
            type="button" 
            className="back-button" 
            onClick={() => navigate('/dashboard')}
            title="Tilbake til oversikten"
          >
            ← Tilbake
          </button>
          <div>
            <h1>{lesson.title}</h1>
            {lesson.description && <p className="lesson-description">{lesson.description}</p>}
          </div>
          <DarkModeToggle />
        </div>
        <div className="lesson-progress">
          Oppgave {currentTaskIndex + 1} av {lesson.tasks.length}
        </div>
      </header>

      <main className="lesson-main">
        {errorMessage && (
          <ErrorCard 
            message={errorMessage} 
            onClose={() => setErrorMessage('')}
          />
        )}

        {currentTask && (
          <div className="task-container">
            <div className="task-layout">
              {/* Left Side: Instructions and Feedback */}
              <div className="task-left-panel">
                <div className="task-info">
                  <h2>Oppgave {currentTask.taskNumber}: {currentTask.description}</h2>
                  <div className="task-instructions">
                    <h3>Instruksjoner:</h3>
                    <div className="instructions-content" dangerouslySetInnerHTML={{ __html: formatInstructions(currentTask.instructions) }} />
                  </div>
                  {currentTask.resources && currentTask.resources.trim() && (
                    <div className="task-resources">
                      <button
                        className="resources-toggle"
                        onClick={() => setShowResources(prev => ({
                          ...prev,
                          [currentTask.taskNumber]: !prev[currentTask.taskNumber]
                        }))}
                      >
                        <span className="resources-toggle-icon">
                          {showResources[currentTask.taskNumber] ? '▼' : '▶'}
                        </span>
                        <span>Ressurser og nyttig informasjon</span>
                      </button>
                      {showResources[currentTask.taskNumber] && (
                        <div className="resources-content">
                          <div className="resources-text">
                            {currentTask.resources.split('\n').map((line, index) => (
                              <p key={index}>{line}</p>
                            ))}
                          </div>
                          
                          <div className="additional-resources-section">
                            {!showResourceRequest[currentTask.taskNumber] ? (
                              <button
                                className="request-additional-resources-btn"
                                onClick={() => setShowResourceRequest(prev => ({
                                  ...prev,
                                  [currentTask.taskNumber]: true
                                }))}
                              >
                                Trenger du mer hjelp? Be om ekstra ressurser
                              </button>
                            ) : (
                              <div className="resource-request-form">
                                <label htmlFor="resource-question">Hva vil du lære mer om?</label>
                                <textarea
                                  id="resource-question"
                                  className="resource-question-input"
                                  value={resourceQuestion[currentTask.taskNumber] || ''}
                                  onChange={(e) => setResourceQuestion(prev => ({
                                    ...prev,
                                    [currentTask.taskNumber]: e.target.value
                                  }))}
                                  placeholder="f.eks. Jeg trenger hjelp med if/else, eller jeg vil lære mer om funksjoner..."
                                  rows="3"
                                  disabled={loadingAdditionalResources[currentTask.taskNumber]}
                                />
                                <div className="resource-request-actions">
                                  <button
                                    className="submit-resource-request-btn"
                                    onClick={handleRequestAdditionalResources}
                                    disabled={loadingAdditionalResources[currentTask.taskNumber] || !resourceQuestion[currentTask.taskNumber]?.trim()}
                                  >
                                    {loadingAdditionalResources[currentTask.taskNumber] ? (
                                      <>
                                        <span className="button-spinner"></span> Genererer...
                                      </>
                                    ) : 'Hent ressurser'}
                                  </button>
                                  <button
                                    className="cancel-resource-request-btn"
                                    onClick={() => {
                                      setShowResourceRequest(prev => ({
                                        ...prev,
                                        [currentTask.taskNumber]: false
                                      }));
                                      setResourceQuestion(prev => ({
                                        ...prev,
                                        [currentTask.taskNumber]: ''
                                      }));
                                    }}
                                    disabled={loadingAdditionalResources[currentTask.taskNumber]}
                                  >
                                    Avbryt
                                  </button>
                                </div>
                              </div>
                            )}

                            {additionalResources[currentTask.taskNumber] && additionalResources[currentTask.taskNumber].length > 0 && (
                              <div className="additional-resources-list">
                                <h4>Ekstra ressurser:</h4>
                                {additionalResources[currentTask.taskNumber].map((resource, index) => (
                                  <div key={index} className="additional-resource-item">
                                    <div className="resource-question-header">
                                      <strong>Ditt spørsmål:</strong> {resource.question}
                                    </div>
                                    <div className="resource-answer">
                                      {resource.answer.split('\n').map((line, lineIndex) => (
                                        <p key={lineIndex}>{line}</p>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {currentValidation && (
                  <div className={`validation-result ${currentValidation.isValid ? 'valid' : 'invalid'}`}>
                    <button 
                      className="validation-close-btn"
                      onClick={() => setValidationResults(prev => {
                        const newResults = { ...prev };
                        delete newResults[currentTask.taskNumber];
                        return newResults;
                      })}
                      title="Lukk"
                    >
                      ×
                    </button>
                    <div className="validation-header">
                      <h3>{currentValidation.isValid ? '✓ Riktig!' : '✗ Trenger forbedring'}</h3>
                      {submissions[currentTask.taskNumber]?.attempts > 0 && (
                        <span className="attempts-badge">Forsøk: {submissions[currentTask.taskNumber].attempts}</span>
                      )}
                    </div>
                    <p>{currentValidation.feedback}</p>
                    {currentValidation.suggestions && currentValidation.suggestions.length > 0 && (
                      <div className="suggestions">
                        <h4>Forslag:</h4>
                        <ul>
                          {currentValidation.suggestions.map((suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="lesson-navigation">
                  <button 
                    onClick={handlePrevious} 
                    disabled={currentTaskIndex === 0}
                    className="nav-btn"
                  >
                    Forrige
                  </button>
                  <button 
                    onClick={handleSubmit}
                    disabled={validating}
                    className="submit-btn"
                  >
                    {validating ? (
                      <>
                        <span className="button-spinner"></span>
                        Validerer...
                      </>
                    ) : (
                      'Lever og valider'
                    )}
                  </button>
                  <button 
                    onClick={handleNext} 
                    disabled={false}
                    className={currentTaskIndex === lesson.tasks.length - 1 ? "complete-btn" : "nav-btn"}
                  >
                    {currentTaskIndex === lesson.tasks.length - 1 ? 'Vis oppsummering' : 'Neste'}
                  </button>
                </div>
              </div>

              {/* Right Side: Code Editor */}
              <div className="task-right-panel">
                <div className="code-section">
                  <div className="code-header">
                    <h3>
                      {lesson.language === 'mixed' 
                        ? 'Koden din (HTML / CSS / JavaScript)' 
                        : `Koden din (${lesson.language.toUpperCase()})`}
                    </h3>
                  <div className="code-header-actions">
                    <button
                      className="run-button"
                      onClick={handleRunCode}
                      disabled={validating || isRunning[currentTask.taskNumber]}
                      title="Kjør koden og vis output"
                    >
                      {isRunning[currentTask.taskNumber] ? 'Kjører...' : 'Kjør kode'}
                    </button>
                    <button
                      className="reset-button"
                      onClick={handleReset}
                      disabled={validating}
                      title="Tilbakestill til startkode"
                    >
                      Tilbakestill
                    </button>
                    <button
                      className="help-button"
                      onClick={() => setHintModalOpen(true)}
                      disabled={validating}
                    >
                      Få hjelp
                    </button>
                  </div>
                  </div>
                  <div className="code-editor-wrapper">
                    {lesson.language === 'mixed' ? (
                      <>
                        {validating && (
                          <div className="validating-overlay">
                            <div className="validating-message">
                              <span className="spinner"></span>
                              Validerer koden din...
                            </div>
                          </div>
                        )}
                        <MultiCodeEditor
                          htmlCode={htmlCode[currentTask.taskNumber] || ''}
                          cssCode={cssCode[currentTask.taskNumber] || ''}
                          jsCode={jsCode[currentTask.taskNumber] || ''}
                          onHtmlChange={handleHtmlChange}
                          onCssChange={handleCssChange}
                          onJsChange={handleJsChange}
                          htmlStarter={currentTask.starterCodeHTML || currentTask.starterCode || ''}
                          cssStarter={currentTask.starterCodeCSS || ''}
                          jsStarter={currentTask.starterCodeJS || ''}
                          height="100%"
                        />
                      </>
                    ) : (
                      <>
                        {validating && (
                          <div className="validating-overlay">
                            <div className="validating-message">
                              <span className="spinner"></span>
                              Validerer koden din...
                            </div>
                          </div>
                        )}
                        <CodeEditor
                          value={code[currentTask.taskNumber] || ''}
                          onChange={handleCodeChange}
                          language={lesson.language}
                          height="100%"
                        />
                      </>
                    )}
                  </div>
                </div>
                
                {/* Console Output Panel */}
                <div className="console-panel">
                  <div className="console-header">
                    <h4>Konsoll</h4>
                    <button
                      className="clear-console-button"
                      onClick={() => setConsoleOutput(prev => {
                        const newOutput = { ...prev };
                        delete newOutput[currentTask.taskNumber];
                        return newOutput;
                      })}
                      title="Tøm konsoll"
                    >
                      Tøm
                    </button>
                  </div>
                  <div className="console-output">
                    {consoleOutput[currentTask.taskNumber] && consoleOutput[currentTask.taskNumber].length > 0 ? (
                      consoleOutput[currentTask.taskNumber].map((output, index) => (
                        <div key={index} className={`console-line console-${output.type}`}>
                          <span className="console-type">{output.type}:</span>
                          <span className="console-message">{output.message}</span>
                        </div>
                      ))
                    ) : (
                      <div className="console-empty">Ingen output ennå. Klikk "Kjør kode" for å kjøre koden din.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <HintModal
        isOpen={hintModalOpen}
        onClose={() => setHintModalOpen(false)}
        onRequestHint={handleRequestHint}
        loading={hintLoading}
        hints={currentHints}
      />
    </div>
  );
};

export default ProgrammingLesson;
